class ComponentScroll extends Theme.Component {
  #state = "idle";
  #drag_start = 0;
  #drag_threshold = 10;
  #drag_delta = 0;
  #scroll_start = 0;
  #animation_frame_id = null;
  #snap_points = [];
  #resize_observer = null;
  #initial_center = false;
  #observed = new Set();
  #saved_position = null;
  #pinned_position = null;
  #autoplay_timer = null;
  #autoplay_paused = false;

  connectedCallback() {
    this.defineElements({
      track: ":scope > .scroll-track",
      dots: ":scope > .scroll-dots",
      button_prev: ":scope > .scroll-button-left",
      button_next: ":scope > .scroll-button-right"
    });

    this.addActionEvents();

    this.refresh();

    requestAnimationFrame(() => {
      this.setStyles();
      this.setInitialCenter();
      this.setSnapPoints();
      this.classList.add("state--ready");
    });

    this.#resize_observer = new ResizeObserver(Theme.Helpers.debounce(this.refresh.bind(this), 150));

    if (this.elements.track) {
      this.#resize_observer.observe(this.elements.track);
    }

    this.observeItems();

    this.addDirectEvent("scroll", this.elements.track, () => this.queueScrollPosition());
    this.addDirectEvent("scroll", this.elements.track, Theme.Helpers.throttle(this.onScroll.bind(this), 50));
    this.addDirectEvent("scroll", this.elements.track, Theme.Helpers.debounce(this.onScroll.bind(this), 100));
    this.addDirectEvent("pointerdown", this.elements.track, this.onPointerDown.bind(this));
    this.addDirectEvent("pointermove", this.elements.track, this.onPointerMove.bind(this));
    this.addDirectEvent("pointerup", this.elements.track, this.onPointerUp.bind(this));
    this.addDirectEvent("pointercancel", this.elements.track, this.onPointerUp.bind(this));

    this.addDirectEvent("dragstart", this.elements.track, (event) => {
      event.preventDefault();
    });

    this.setupAutoplay();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.stopAutoplay();
    this.#resize_observer?.disconnect();
    this.#resize_observer = null;
    this.#observed.clear();
  }

  refresh() {
    this.observeItems();
    this.setStyles();
    this.setInitialCenter();
    this.setSnapPoints();
  }

  items() {
    return Array.from(this.querySelectorAll(":scope > .scroll-track > *"));
  }

  observeItems() {
    if (!this.#resize_observer) {
      return;
    }

    const items = this.items();

    items.forEach((item) => {
      if (this.#observed.has(item)) {
        return;
      }

      this.#resize_observer.observe(item);
      this.#observed.add(item);
    });

    this.#observed.forEach((item) => {
      if (items.includes(item)) {
        return;
      }

      this.#resize_observer.unobserve(item);
      this.#observed.delete(item);
    });
  }

  setStyles() {
    const isScrollable = this.isScrollable();

    if (isScrollable) {
      this.classList.add("state--scrollable");
      this.updateScrollbarCompensation();
    } else {
      this.classList.remove("state--scrollable");
    }
  }

  updateScrollbarCompensation() {
    const scrollbar_width = window.innerWidth - document.documentElement.clientWidth;

    if (scrollbar_width > 0) {
      this.style.setProperty("--scrollbar-compensation", `${scrollbar_width / 2}px`);
    } else {
      this.style.removeProperty("--scrollbar-compensation");
    }
  }

  pinPosition(value) {
    this.#pinned_position = value;
  }

  rememberPosition() {
    const track = this.elements.track;

    if (track && this.#pinned_position === null) {
      this.#saved_position = track.scrollLeft;
    }
  }

  restorePosition() {
    const track = this.elements.track;

    if (!track) {
      return;
    }

    if (this.#pinned_position !== null) {
      track.scrollLeft = this.#pinned_position;
      return;
    }

    if (this.#saved_position === null) {
      this.setInitialCenter(true);
      return;
    }

    track.scrollLeft = this.#saved_position;
  }

  setInitialCenter(force = false) {
    const track = this.elements.track;

    if (this.#initial_center && !force) {
      return;
    }

    if (!track || !track.classList.contains("scroll--center")) {
      return;
    }

    if (!this.classList.contains("state--scrollable")) {
      return;
    }

    if (!force && track.scrollLeft > 0) {
      this.#initial_center = true;
      return;
    }

    track.scrollLeft = (track.scrollWidth - track.clientWidth) / 2;
    this.#initial_center = true;
  }

  setSnapPoints() {
    const track = this.elements.track;

    if (!track) {
      this.#snap_points = [];
      return;
    }

    const container_box = track.getBoundingClientRect();
    const container_style = getComputedStyle(track);
    const container_padding_left = parseFloat(container_style.paddingLeft) || 0;
    const container_padding_right = parseFloat(container_style.paddingRight) || 0;
    const container_edge = container_box.left + container_padding_left;
    const container_scroll = track.scrollLeft;
    const is_centered = track.classList.contains("scroll--center");
    const inner_width = track.clientWidth - container_padding_left - container_padding_right;
    const max = track.scrollWidth - track.clientWidth;

    const points = [];

    this.items().forEach((element) => {
      const element_box = element.getBoundingClientRect();
      const item_scroll = element_box.left - container_edge;

      let distance = container_scroll + item_scroll;

      if (is_centered) {
        distance = distance - ((inner_width - element.clientWidth) / 2);
      }

      const clamped = Math.max(0, Math.min(distance, max));
      const rounded = Math.round(clamped);

      if (!points.includes(rounded)) {
        points.push(rounded);
      }
    });

    this.#snap_points = points;
    this.buildDots();
    this.updateActiveDot();
    this.updateButtonVisibility();
  }

  buildDots() {
    const dots_el = this.elements.dots;
    if (!dots_el) return;

    dots_el.innerHTML = "";

    const snap_count = this.#snap_points.length;
    if (snap_count <= 1) return;

    const group_size = this.getGroupSize();
    const num_dots = Math.max(2, Math.ceil(snap_count / group_size));
    const snaps_per_dot = Math.ceil(snap_count / num_dots);

    for (let i = 0; i < num_dots; i++) {
      const dot = document.createElement("button");
      dot.type = "button";
      dot.classList.add("scroll-dot");
      dot.setAttribute("aria-label", `Go to item ${i + 1}`);
      const snap_index = Math.min(i * snaps_per_dot, snap_count - 1);
      dot.addEventListener("click", () => {
        this.animateScrollTo(this.#snap_points[snap_index]);
      });
      dots_el.appendChild(dot);
    }

    this.updateScrollPosition();
  }

  queueScrollPosition() {
    if (this.position_frame) return;

    this.position_frame = requestAnimationFrame(() => {
      this.position_frame = null;
      this.updateScrollPosition();
    });
  }

  updateScrollPosition() {
    const dots_el = this.elements.dots;
    const track = this.elements.track;
    if (!dots_el || !track) return;

    const count = dots_el.querySelectorAll(".scroll-dot").length;
    const range = track.scrollWidth - track.clientWidth;
    const progress = range > 0 ? track.scrollLeft / range : 0;
    const position = progress * Math.max(count - 1, 0);
    const index = Math.min(Math.floor(position), Math.max(count - 2, 0));
    const travel = position - index;

    dots_el.style.setProperty("--worm-start", index + Math.max(0, (travel - 0.5) * 2));
    dots_el.style.setProperty("--worm-end", index + Math.min(1, travel * 2));
  }

  onScroll() {
    this.updateActiveDot();
    this.updateButtonVisibility();
  }

  updateActiveDot() {
    const dots_el = this.elements.dots;
    if (!dots_el) return;

    const scroll_left = this.elements.track?.scrollLeft ?? 0;
    const num_dots = dots_el.querySelectorAll(".scroll-dot").length;
    const snap_count = this.#snap_points.length;
    const snaps_per_dot = num_dots ? Math.ceil(snap_count / num_dots) : 1;

    let closest_snap = 0;
    let min_dist = Infinity;
    this.#snap_points.forEach((point, i) => {
      const dist = Math.abs(point - scroll_left);
      if (dist < min_dist) {
        min_dist = dist;
        closest_snap = i;
      }
    });

    const active_dot = Math.min(Math.floor(closest_snap / snaps_per_dot), num_dots - 1);

    dots_el.style.setProperty("--scroll-dot-count", num_dots);
    dots_el.style.setProperty("--scroll-dot-index", active_dot);

    dots_el.querySelectorAll(".scroll-dot").forEach((dot, i) => {
      const is_active = i === active_dot;
      dot.classList.toggle("state--active", is_active);
      dot.toggleAttribute("aria-current", is_active);
    });
  }

  updateButtonVisibility() {
    if (!this.#snap_points.length) return;

    const scroll_left = this.elements.track?.scrollLeft ?? 0;
    const tolerance = 2;
    const first = this.#snap_points[0];
    const last = this.#snap_points[this.#snap_points.length - 1];

    const prev_hidden = scroll_left <= first + tolerance;
    const next_hidden = scroll_left >= last - tolerance;

    this.classList.toggle("state--start", prev_hidden);
    this.classList.toggle("state--end", next_hidden);

    this.elements.button_prev?.classList.toggle("state--hidden", prev_hidden);
    this.elements.button_prev?.toggleAttribute("disabled", prev_hidden);
    this.elements.button_next?.classList.toggle("state--hidden", next_hidden);
    this.elements.button_next?.toggleAttribute("disabled", next_hidden);
  }

  isScrollable() {
    const track = this.elements.track;
    return track ? (track.scrollWidth - track.clientWidth) > 1 : false;
  }

  onPointerDown(event) {
    if (event.pointerType !== "mouse") {
      return;
    }

    if (event.button !== 0) {
      return;
    }

    this.#state = "tracking";
    this.#scroll_start = this.elements.track?.scrollLeft ?? 0;
    this.#drag_start = event.pageX;
  }

  onPointerMove(event) {
    if (event.pointerType !== "mouse") {
      return;
    }

    if (this.#state === "idle") {
      return;
    }

    this.#drag_delta = event.pageX - this.#drag_start;

    if (this.isDrag() && this.#state === "tracking") {
      this.elements.track?.setPointerCapture(event.pointerId);
      this.#state = "dragging";
      this.classList.add("state--dragging");
      this.startAnimationLoop();
    }
  }

  onPointerUp(event) {
    if (event.pointerType === "touch") {
      this.reset();
      return;
    }

    const distance = Math.abs((this.elements.track?.scrollLeft ?? 0) - this.#scroll_start);

    if (this.#state === "dragging" && event.pointerType === "mouse") {
      this.preventDefaultClick();
    }

    if (this.#state === "dragging" || distance >= this.#drag_threshold) {
      this.animateScrollTo(this.getDirectionalSnapPoint());
    }

    this.reset();
  }

  isDrag() {
    return Math.abs(this.#drag_delta) >= this.#drag_threshold;
  }

  startAnimationLoop() {
    if (this.#animation_frame_id) {
      return;
    }

    const loop = () => {
      if (this.elements.track) {
        this.elements.track.scrollLeft = this.#scroll_start - this.#drag_delta;
      }
      this.#animation_frame_id = requestAnimationFrame(loop);
    };

    this.#animation_frame_id = requestAnimationFrame(loop);
  }

  stopAnimationLoop() {
    cancelAnimationFrame(this.#animation_frame_id);
    this.#animation_frame_id = null;
  }

  preventDefaultClick() {
    this.elements.track?.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
    }, {
      capture: true,
      once: true
    });
  }

  animateScrollTo(value) {
    const reduce_motion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    this.elements.track?.scrollTo({
      left: value,
      behavior: reduce_motion ? "auto" : "smooth"
    });
  }

  getDirectionalSnapPoint() {
    const container_scroll = this.elements.track?.scrollLeft ?? 0;

    if (!this.#snap_points.length) {
      return container_scroll;
    }

    const direction = Math.sign(container_scroll - this.#scroll_start);

    if (direction > 0) {
      return this.getNextSnapPoint();
    }

    if (direction < 0) {
      return this.getPreviousSnapPoint();
    }

    return container_scroll;
  }

  getNextSnapPoint() {
    const points = this.#snap_points;
    const scroll_left = this.elements.track?.scrollLeft ?? 0;
    const next = points.find((p) => p > scroll_left);

    return next ?? points[points.length - 1];
  }

  getPreviousSnapPoint() {
    const points = this.#snap_points;
    const scroll_left = this.elements.track?.scrollLeft ?? 0;
    const previous = [...points].reverse().find((p) => p < scroll_left);

    return previous ?? points[0];
  }

  nextItem() {
    this.animateScrollTo(this.getNextSnapPoint());
  }

  previousItem() {
    this.animateScrollTo(this.getPreviousSnapPoint());
  }

  nextGroup() {
    if (!this.#snap_points.length) return;

    const group_size = this.getGroupSize();
    const tolerance = 0.5;
    const scroll_left = this.elements.track?.scrollLeft ?? 0;

    let current_index = this.#snap_points.findIndex((point) => {
      return point >= (scroll_left - tolerance);
    });

    if (current_index === -1) {
      current_index = this.#snap_points.length - 1;
    }

    const index = Math.min(current_index + group_size, this.#snap_points.length - 1);

    this.animateScrollTo(this.#snap_points[index]);
  }

  previousGroup() {
    if (!this.#snap_points.length) return;

    const group_size = this.getGroupSize();
    const tolerance = 0.5;
    const scroll_left = this.elements.track?.scrollLeft ?? 0;

    let current_index = this.#snap_points.findIndex((point) => {
      return point >= (scroll_left - tolerance);
    });

    if (current_index === -1) {
      current_index = this.#snap_points.length - 1;
    }

    const index = Math.max(current_index - group_size, 0);

    this.animateScrollTo(this.#snap_points[index]);
  }

  getGroupSize() {
    const track = this.elements.track;
    const items = this.items();

    if (!track || !items.length) return 1;

    const container_width = track.clientWidth;
    const item_width = items[0].clientWidth;

    return item_width ? Math.max(1, Math.floor(container_width / item_width)) : 1;
  }

  get autoplayInterval() {
    const seconds = parseFloat(this.dataset.autoplay);

    return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : null;
  }

  get prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  setupAutoplay() {
    if (!this.autoplayInterval || this.prefersReducedMotion) {
      return;
    }

    this.addDirectEvent("pointerenter", this, (event) => {
      if (event.pointerType === "mouse") {
        this.pauseAutoplay();
      }
    });

    this.addDirectEvent("pointerleave", this, (event) => {
      if (event.pointerType === "mouse") {
        this.resumeAutoplay();
      }
    });

    this.addDirectEvent("focusin", this, this.pauseAutoplay.bind(this));
    this.addDirectEvent("focusout", this, this.resumeAutoplay.bind(this));
    this.addDirectEvent("pointerdown", this, this.restartAutoplay.bind(this));
    this.addDirectEvent("wheel", this, this.restartAutoplay.bind(this));

    this.addDirectEvent("visibilitychange", document, () => {
      if (document.hidden) {
        this.pauseAutoplay();
      } else {
        this.resumeAutoplay();
      }
    });

    this.intersectionObserver((entry) => {
      if (entry.isIntersecting) {
        this.startAutoplay();
      } else {
        this.stopAutoplay();
      }
    });
  }

  startAutoplay() {
    const interval = this.autoplayInterval;

    if (this.#autoplay_timer || !interval) {
      return;
    }

    this.#autoplay_timer = setInterval(() => {
      if (!this.#autoplay_paused) {
        this.advanceAutoplay();
      }
    }, interval);
  }

  stopAutoplay() {
    clearInterval(this.#autoplay_timer);
    this.#autoplay_timer = null;
  }

  restartAutoplay() {
    if (!this.#autoplay_timer) {
      return;
    }

    this.stopAutoplay();
    this.startAutoplay();
  }

  pauseAutoplay() {
    this.#autoplay_paused = true;
  }

  resumeAutoplay() {
    this.#autoplay_paused = false;
  }

  advanceAutoplay() {
    if (!this.isScrollable() || this.#snap_points.length < 2) {
      return;
    }

    const scroll_left = this.elements.track?.scrollLeft ?? 0;
    const last = this.#snap_points[this.#snap_points.length - 1];

    if (scroll_left >= last - 2) {
      this.animateScrollTo(this.#snap_points[0]);
      return;
    }

    this.nextGroup();
  }

  reset() {
    this.stopAnimationLoop();
    this.#state = "idle";
    this.#drag_start = 0;
    this.#scroll_start = 0;
    this.classList.remove("state--dragging");
  }
}

customElements.define("component-scroll", ComponentScroll);
